import { useCallback, useEffect, useMemo, useRef } from 'react'

import { errorView, type SaleVerdict } from '@krp/client-runtime'
import type { ParkActions, PaymentKind, PickerRowVm, ProgressToolActions, ReceiptRetryActions, SaleActions, SaleSession } from '@krp/sale-ui'

import type { OwnDispatch, OwnStore } from '../../../state'
import type { Cashier, WordOutcome } from '../../agent-session/model/agentSessionController'
import type { AgentSession } from '../../agent-session/model/useAgentSession'
import {
  operationOf,
  ownNoticeAdded,
  ownNoticeRemoved,
  ownOperationChanged,
  ownOperationWindowClosed,
  ownParkClosed,
  ownParkOpened,
  ownSending,
  ownToolClosed,
  ownToolField,
  ownToolOpened,
  ownWordAccepted,
  ownWordRefused,
} from '../../operation/model/operationSlice'
import { DOUBLE_RECEIPT_TEXT, isReconcileCode } from '../../operation/model/progress'
import { parseDocumentNumber, toolErrors, type OwnNotice, type OwnSending, type OwnToolKey } from '../../operation/model/words'
import { cartOfSnapshot, snapshotMatches } from '../../snapshot/model/snapshot'
import { refusedNoticeOf } from '../../catalog-lines/model/saleLine'
import type { OwnSnapshotPort } from '../../snapshot/model/snapshotPort'
import { cardOnly, cashOnly } from './payment'
import { buildReceiptDraft } from './draft'
import { cartTotal, type SaleProduct } from './cart'
import { ownPaymentAborted, ownPaymentAttemptStarted, ownPaymentClosed, ownPaymentCollapsed, ownPaymentExpanded, paymentAttemptOf } from './paymentAttemptSlice'
import {
  ownCartRestored,
  ownContactChanged,
  ownLineRemoved,
  ownLineSelected,
  ownNewSaleStarted,
  ownPayAborted,
  ownPaymentKindChosen,
  ownPayStarted,
  ownPriceTypeChosen,
  ownProductAdded,
  ownQuantityStepped,
  ownQuantityTyped,
  ownSaleCleared,
  ownSaleParked,
  ownSaleSettled,
  ownSaleWindowClosed,
  ownSearchChanged,
  saleOf,
} from './saleSlice'
import { buildOwnSaleVm, unresolvedOperationId, type OwnCatalogView } from './saleVm'

/**
 * Продажа источника «Собственная» для React: корзина (редьюсер) + сессия кассового агента → `SaleSession` общего экрана.
 * Агент и каталог приходят снаружи (composition), поэтому хук проверяется подставными, без сети и без Redux.
 *
 * Деньги: оплата и чек у агента — ОДНА команда, повтор после обрыва идёт с тем же ключом (это делает контроллер сессии); хук ничего
 * не повторяет сам. Если команду отправить нельзя (продажа закрыта), корзина возвращается как была.
 */

/**
 * Ответ каталога на строку, отправленную Enter (сканер-клавиатура жмёт его сам): строка товара (с причиной, если продать нельзя) и сам
 * товар для корзины, «не найден», «несколько товаров с этим кодом» или сбой запроса словами службы.
 */
export type OwnLookup =
  | { kind: 'row'; row: PickerRowVm; product: SaleProduct | null }
  | { kind: 'not-found' }
  | { kind: 'ambiguous' }
  | { kind: 'failed'; message: string }

export interface OwnCatalog {
  /** Что показать в поиске и «☰ Каталог» для набранного текста, и как найти товар по ключу для корзины. */
  view: OwnCatalogView
  find: (productKey: string) => SaleProduct | null
  /**
   * Товар ровно по отправленной строке (К-117), а не первая строка выдачи, которая ещё не пришла: код из цифр — только точный штрихкод или
   * артикул, наименование — первый товар из базы источника. Не бросает: сбой — `failed`.
   */
  lookup: (text: string) => Promise<OwnLookup>
  /** Виды цен базы 1С, из которых кассир выбирает. */
  priceTypes: readonly { key: string; name: string }[]
  /** Вид цены по умолчанию из настроек источника; `null` — кассир выбирает сам. */
  defaultPriceTypeKey: string | null
}

export interface UseOwnSaleOptions {
  resourceId: string
  /** Состояние окна (слайсы `sale` и `operation`) и dispatch приложения: корзина и ход операции живут в store, а не в хуке. */
  store: OwnStore
  agent: AgentSession
  /**
   * Каталог для набранного в поиске текста: хук, потому что текст живёт в состоянии продажи, а каталог его читает. `dispatch` приложения —
   * для поиска ровно по отправленной строке (`lookup`).
   */
  useCatalog: (resourceId: string, searchText: string, priceTypeKey: string | null, dispatch: OwnDispatch) => OwnCatalog
  /**
   * Подтверждённый кассир (RP-OWN11). `null` — подтверждения нет: новую оплату хук не начинает; допускается только повтор начатой
   * операции (корзина та же, что в снимке), он уходит с телом из снимка и кассиром снимка.
   */
  cashier: Cashier | null
  /** Снимок строк чека на начало операции (RP-OWN4); нет порта — запрос агенту собирается как есть. */
  snapshot?: OwnSnapshotPort
}

const RESTORED_NOTICE_KEY = 'restored'
/** Сообщение о строке поиска, которую не удалось положить в чек: висит, пока кассир не закроет; следующее заменяет его. */
const SEARCH_NOTICE_KEY = 'sale-search'

function lookupNoticeOf(text: string, found: Exclude<OwnLookup, { kind: 'row' }>): OwnNotice {
  const words =
    found.kind === 'not-found'
      ? `Ничего не найдено: «${text}». Проверьте штрихкод или наименование.`
      : found.kind === 'ambiguous'
        ? `Код ${text} есть у нескольких товаров — выберите нужный в «☰ Каталог».`
        : `Поиск товара «${text}» не ответил: ${found.message} Товар не добавлен — отсканируйте его ещё раз.`
  return { key: SEARCH_NOTICE_KEY, text: words, tone: 'warning', action: null }
}
const RESTORED_TEXT = 'Незавершённая операция: корзина восстановлена из начатой оплаты (цены и ставки как при её начале). Нажмите «Оплатить» — повтор уйдёт с тем же ключом операции, второго списания не будет.'

const keyOf = (key: string): number => Number(key)

/** Сессия продажи и то, что окну нужно сверх экрана: виды цен и выбор вида цены кассиром. */
export interface OwnSaleSession extends SaleSession {
  priceTypes: readonly { key: string; name: string }[]
  /** Действующий вид цены: выбор кассира, иначе по умолчанию; `null` — не выбран. */
  priceTypeKey: string | null
  /** Вид цены можно менять: корзина пуста и идёт сборка. */
  priceTypeLockedReason: string | null
  choosePriceType: (key: string) => void
}

export function useOwnSale(options: UseOwnSaleOptions): OwnSaleSession {
  const { agent, cashier, resourceId, snapshot, store, useCatalog } = options
  const { dispatch } = store
  const state = saleOf(store.state.sale, resourceId)
  const words = operationOf(store.state.operation, resourceId)
  const attempt = paymentAttemptOf(store.state.paymentAttempt, resourceId)
  const catalog = useCatalog(resourceId, state.searchText, state.priceTypeKey, dispatch)
  const { actions: agentActions, model } = agent

  // Окно закрыто: корзина и формы этого окна в следующее не переходят (после F5 корзину возвращает снимок, а не слайс).
  useEffect(
    () => () => {
      dispatch(ownSaleWindowClosed({ resourceId }))
      dispatch(ownOperationWindowClosed({ resourceId }))
    },
    [dispatch, resourceId],
  )

  // Чек пробит или не пробит наверняка: снимок строк больше не нужен. Неизвестный исход его сохраняет (повтор и восстановление).
  const clearSnapshot = snapshot?.clear
  useEffect(() => {
    if (state.phase === 'finished' || state.phase === 'failed') {
      clearSnapshot?.()
      dispatch(ownNoticeRemoved({ resourceId, key: RESTORED_NOTICE_KEY }))
    }
  }, [clearSnapshot, dispatch, resourceId, state.phase])

  // После F5 корзина пуста, а снимок начатой операции остался: возвращаем её ровно такой, какой начали (цена, ставка, профиль и
  // версия карточки — из снимка, карточки не перечитываются) и говорим человеку, что это незавершённая операция. Повтор «Оплатить»
  // берёт запрос из снимка (`snapshot.begin`), поэтому уходит с тем же ключом операции. Один раз за жизнь окна.
  const current = snapshot?.current ?? null
  const restored = useRef<'off' | 'pending' | 'shown'>('off')
  useEffect(() => {
    if (restored.current !== 'off' || current === null || state.phase !== 'composing' || state.cart.lines.length > 0) return
    restored.current = 'pending'
    dispatch(ownCartRestored({ resourceId, cart: cartOfSnapshot(current), paymentKind: current.paymentKind }))
    dispatch(ownNoticeAdded({ resourceId, notice: { key: RESTORED_NOTICE_KEY, text: RESTORED_TEXT, tone: 'warning', action: null } }))
  }, [current, dispatch, resourceId, state.cart.lines.length, state.phase])

  // Восстановленную корзину сбросили («Очистить»): снимок больше не описывает ничего, иначе следующий F5 вернул бы её снова.
  useEffect(() => {
    if (restored.current === 'pending' && state.cart.lines.length > 0) restored.current = 'shown'
    else if (restored.current === 'shown' && state.phase === 'composing' && state.cart.lines.length === 0) {
      restored.current = 'off'
      clearSnapshot?.()
      dispatch(ownNoticeRemoved({ resourceId, key: RESTORED_NOTICE_KEY }))
    }
  }, [clearSnapshot, dispatch, resourceId, state.cart.lines.length, state.phase])

  // Исход, неизвестный после обрыва, агент снимает сам, когда вернётся (или кассир нажал «Проверить»): итог приходит в `model.last`.
  useEffect(() => {
    if (state.phase === 'unknown' && model.last !== null && model.last !== state.verdict &&
      state.verdict?.meta?.operationId !== undefined && model.last.meta?.operationId === state.verdict.meta.operationId) dispatch(ownSaleSettled({ resourceId, verdict: model.last }))
  }, [dispatch, model.last, resourceId, state.phase, state.verdict])

  /** Заметки после пробитого чека: допечатать, возможно два чека, слова агента. Висят, пока кассир не нажмёт «Понятно» или «Допечатать». */
  const noticesAfter = useCallback((verdict: SaleVerdict): void => {
    if (verdict.kind !== 'sold' || verdict.meta === undefined) return
    const meta = verdict.meta
    const add = (notice: OwnNotice): void => void dispatch(ownNoticeAdded({ resourceId, notice }))
    if (meta.printed === false) {
      add({ key: 'print', text: meta.notice ?? 'Чек пробит, но не напечатан. Проверьте ленту.', tone: 'warning', action: 'print' })
    } else if (meta.notice !== null) {
      add({ key: 'info', text: meta.notice, tone: 'neutral', action: null })
    }
    if (meta.possibleDoubleReceipt) add({ key: 'double', text: DOUBLE_RECEIPT_TEXT, tone: 'danger', action: null })
  }, [dispatch, resourceId])

  const settle = useCallback(
    (verdict: SaleVerdict | null): void => {
      if (verdict === null) return
      dispatch(ownSaleSettled({ resourceId, verdict }))
      noticesAfter(verdict)
    },
    [dispatch, noticesAfter, resourceId],
  )

  // Начата другая продажа или операция: формы и отказ прежней не переносим.
  const operationId = unresolvedOperationId(state, model)
  useEffect(() => {
    dispatch(ownOperationChanged({ resourceId }))
  }, [dispatch, operationId, resourceId])

  /** Итог слова человека: отказ агента остаётся рядом с кнопкой его же словами, принятое закрывает формы. */
  const applyWord = useCallback(
    (outcome: WordOutcome | null, forOperation: string): void => {
      if (outcome === null) {
        dispatch(ownSending({ resourceId, what: null }))
        return
      }
      if (outcome.kind === 'refused') {
        dispatch(ownWordRefused({ resourceId, operationId: forOperation, code: outcome.code, text: outcome.text }))
        return
      }
      dispatch(ownWordAccepted({ resourceId }))
      const verdict = outcome.verdict
      const parking = verdict.kind === 'unknown' ? (verdict.meta?.parking ?? null) : null
      if (parking !== null) {
        dispatch(ownSaleParked({ resourceId }))
        dispatch(
          ownNoticeAdded({
            resourceId,
            notice: { key: 'parked', text: parking.warning ?? 'Продажа отложена: деньги взяты, чек не пробит. Запись хранится в кассовом агенте.', tone: 'neutral', action: null },
          }),
        )
        return
      }
      settle(verdict)
    },
    [dispatch, resourceId, settle],
  )

  // Слово человека уходит агенту ровно один раз на нажатие: второе нажатие в ту же долю секунды (до перерисовки) гасится флагом.
  const inFlight = useRef(false)
  const begin = useCallback((): boolean => {
    if (inFlight.current) return false
    inFlight.current = true
    return true
  }, [])

  const word = useCallback(
    (what: OwnSending, call: (id: string) => Promise<WordOutcome | null>): void => {
      if (operationId === null || words.sending !== null || !begin()) return
      dispatch(ownSending({ resourceId, what }))
      void call(operationId)
        .then((outcome) => applyWord(outcome, operationId))
        .finally(() => {
          inFlight.current = false
        })
    },
    [applyWord, begin, dispatch, operationId, resourceId, words.sending],
  )

  const repeat = useCallback((): void => {
    if (operationId === null || words.sending !== null) return
    // «Сверить с кассой» вместо повтора, когда агент не может исключить документ на кассе: сначала читаем накопитель.
    if (words.refusal !== null && words.refusal.operationId === operationId && isReconcileCode(words.refusal.code)) {
      if (!begin()) return
      dispatch(ownSending({ resourceId, what: 'repeat' }))
      void agentActions
        .recover(operationId)
        .then((verdict) => {
          dispatch(ownWordAccepted({ resourceId }))
          settle(verdict)
        })
        .finally(() => {
          inFlight.current = false
        })
      return
    }
    word('repeat', (id) => agentActions.repunch(id))
  }, [agentActions, begin, dispatch, operationId, resourceId, settle, word, words.refusal, words.sending])

  const submitTool = useCallback(
    (key: string): void => {
      const tool = words.tool
      if (tool === null || tool.key !== key || Object.keys(toolErrors(tool.key, tool.values)).length > 0) return
      const values = tool.values
      const documentNumber = parseDocumentNumber(values.lastFiscalDocument ?? '') ?? 0
      switch (tool.key) {
        case 'correction': {
          const baseDate = (values.baseDate ?? '').trim()
          const baseNumber = (values.baseNumber ?? '').trim()
          word('correction', (id) =>
            agentActions.correction(id, {
              lastFiscalDocument: documentNumber,
              correctionType: values.correctionType === 'instruction' ? 'instruction' : 'self',
              ...(baseDate === '' ? {} : { baseDate }),
              ...(baseNumber === '' ? {} : { baseNumber }),
            }),
          )
          return
        }
        case 'repunch-number':
          word('repunch-number', (id) => agentActions.repunch(id, documentNumber))
          return
        case 'close': {
          const rrn = (values.rrn ?? '').trim()
          const authCode = (values.authCode ?? '').trim()
          // `checked` и `reason` агент требует в обоих исходах (иначе 400): форма не отправится без них (`toolErrors`).
          const said = { checked: (values.checked ?? '').trim(), reason: (values.reason ?? '').trim() }
          word('close', (id) =>
            agentActions.resolveCard(id, values.charged === 'yes' ? { charged: true, rrn, ...(authCode === '' ? {} : { authCode }), ...said } : { charged: false, ...said }),
          )
          return
        }
        case 'confirm-void':
          word('confirm-void', (id) => agentActions.confirmVoid(id))
          return
      }
    },
    [agentActions, word, words.tool],
  )

  const printTail = useCallback((): void => {
    if (words.sending !== null || !begin()) return
    dispatch(ownSending({ resourceId, what: 'print' }))
    void agentActions.continuePrint().then((outcome) => {
      inFlight.current = false
      dispatch(ownSending({ resourceId, what: null }))
      if (outcome === null) return
      const add = (notice: OwnNotice): void => void dispatch(ownNoticeAdded({ resourceId, notice }))
      if (outcome.kind === 'refused') {
        add({ key: 'print', text: outcome.text, tone: 'danger', action: 'print' })
      } else if (outcome.result.status === 'done') {
        dispatch(ownNoticeRemoved({ resourceId, key: 'print' }))
      } else {
        // «Всё ещё не печатает» и «проверьте ленту»: слова агента дословно, кнопка остаётся.
        const error = outcome.result.error
        add({ key: 'print', text: [error?.text ?? 'Допечатать не удалось.', error?.action ?? ''].filter((part) => part !== '').join(' '), tone: 'warning', action: 'print' })
      }
    })
  }, [agentActions, begin, dispatch, resourceId, words.sending])

  // Оплата в пути: второе нажатие до перерисовки (старое замыкание с `composing`) не уходит. Иначе его отказ «касса занята» вернул бы корзину
  // в правку, итог первой команды потерялся бы, и следующая оплата ушла бы с новым ключом — второе списание.
  const paying = useRef(false)

  const payWith = useCallback(
    (kind: PaymentKind): void => {
      // Кнопки при закрытом пороге и так выключены; эта проверка — на случай устаревшего нажатия. Порог проверит и контроллер.
      // «Повторить оплату» и «Наличными» окна «Не оплачено» / «Оплата не началась» — тот же путь из `failed`: корзина та же, ключ новый.
      const retrying = state.phase === 'failed'
      if (paying.current || (state.phase !== 'composing' && !retrying) || !model.gate.open || model.busy !== null) return
      const total = cartTotal(state.cart)
      // Нет подтверждённого кассира: новая оплата закрыта, повтор начатой операции — по её снимку (то же тело, тот же кассир).
      const started = snapshot?.current ?? null
      if (cashier === null && (started === null || !snapshotMatches(started, state.cart, kind, total))) return
      const payer = cashier ?? started?.draft.cashier
      if (payer === undefined) return
      const built = buildReceiptDraft({
        type: 'sale',
        cashier: payer,
        cart: state.cart,
        // Сдачи с наличных нет: кнопка «Наличными» принимает ровно итог (ввод суммы — отдельным шагом экрана).
        payment: kind === 'Card' ? cardOnly(total) : cashOnly(total),
        ...(state.contact.trim() === '' ? {} : { customerContact: state.contact }),
      })
      if (!built.ok) return
      paying.current = true
      // Отказ прошлой попытки снимается, и сразу новая попытка — новое окно (RP-PAY-ONE): одно нажатие — одно окно до итога.
      if (retrying) dispatch(ownNewSaleStarted({ resourceId }))
      dispatch(ownPaymentAttemptStarted({ resourceId, kind }))
      dispatch(ownPayStarted({ resourceId, kind, totalKopecks: built.totalKopecks }))
      const draft = snapshot === undefined ? built.draft : snapshot.begin({ cart: state.cart, draft: built.draft, paymentKind: kind, totalKopecks: built.totalKopecks })
      void agentActions.sell(draft).then(
        (verdict) => {
          paying.current = false
          if (verdict === null) {
            // Агент команду не принял до кассы (порог закрылся в момент нажатия): «Оплата не началась» в окне, корзина цела.
            dispatch(ownPayAborted({ resourceId }))
            dispatch(ownPaymentAborted({ resourceId }))
          } else settle(verdict)
        },
        () => {
          paying.current = false
        },
      )
    },
    [agentActions, cashier, dispatch, model.busy, model.gate.open, resourceId, settle, snapshot, state.cart, state.contact, state.phase],
  )

  const reconcile = useCallback((): void => {
    if (operationId === null) return
    void agentActions.recover(operationId).then(settle)
  }, [agentActions, operationId, settle])

  const addByKey = useCallback(
    (productKey: string): void => {
      const found = catalog.find(productKey)
      if (found !== null) dispatch(ownProductAdded({ resourceId, product: found }))
    },
    [catalog, dispatch, resourceId],
  )

  // RP-SY5: товар, который нельзя продать, по скану, Enter или выбору не пропадает молча — причина службы дословно сообщением с
  // копированием; сообщение висит, пока кассир его не закроет.
  const addRow = useCallback(
    (row: PickerRowVm): void => {
      const refused = refusedNoticeOf(row)
      if (refused === null) addByKey(row.productKey)
      else dispatch(ownNoticeAdded({ resourceId, notice: refused }))
    },
    [addByKey, dispatch, resourceId],
  )

  // К-117: очередь строк, отправленных Enter. Каждый скан — ровно его товар и одна штука; поиски идут по одному, в порядке сканов.
  const lookups = useRef<Promise<void>>(Promise.resolve())
  const submitSearch = useCallback((): void => {
    const text = state.searchText.trim()
    if (text === '') return
    // Строка освобождается сразу, а не по ответу: следующий скан печатается в пустую строку и не склеивается с этим кодом.
    dispatch(ownSearchChanged({ resourceId, text: '' }))
    const lookup = catalog.lookup
    lookups.current = lookups.current.then(async () => {
      // Порт обещает не бросать; если всё же бросил — это сбой поиска словами, а очередь идёт дальше, а не встаёт.
      const found = await lookup(text).catch((error: unknown): OwnLookup => ({ kind: 'failed', message: errorView(error).message }))
      if (found.kind !== 'row') {
        dispatch(ownNoticeAdded({ resourceId, notice: lookupNoticeOf(text, found) }))
        return
      }
      const refused = refusedNoticeOf(found.row)
      if (refused !== null) dispatch(ownNoticeAdded({ resourceId, notice: refused }))
      else if (found.product !== null) dispatch(ownProductAdded({ resourceId, product: found.product }))
      // Скан не пропадает молча: строка продаваема, а товара для корзины служба не отдала.
      else dispatch(ownNoticeAdded({ resourceId, notice: { key: SEARCH_NOTICE_KEY, text: `«${found.row.name}»: цена и ставка НДС пока недоступны — товар не добавлен.`, tone: 'warning', action: null } }))
    })
  }, [catalog.lookup, dispatch, resourceId, state.searchText])

  const parkActions = useMemo<ParkActions>(
    () => ({
      open: () => void dispatch(ownParkOpened({ resourceId })),
      reasonChanged: () => undefined,
      submit: () => word('park', (id) => agentActions.park(id)),
      close: () => void dispatch(ownParkClosed({ resourceId })),
    }),
    [agentActions, dispatch, resourceId, word],
  )

  // Повтор без формы: одно нажатие. Остальные поля интерфейса формы повтора этому источнику не нужны.
  const retryActions = useMemo<ReceiptRetryActions>(
    () => ({
      repeat,
      open: repeat,
      checkedChanged: () => undefined,
      checkedTextChanged: () => undefined,
      reasonChanged: () => undefined,
      submit: () => undefined,
      close: () => undefined,
    }),
    [repeat],
  )

  const toolActions = useMemo<ProgressToolActions>(
    () => ({
      run: () => undefined,
      open: (key) => void dispatch(ownToolOpened({ resourceId, key: key as OwnToolKey })),
      fieldChanged: (_key, field, value) => void dispatch(ownToolField({ resourceId, field, value })),
      submit: submitTool,
      close: () => void dispatch(ownToolClosed({ resourceId })),
    }),
    [dispatch, resourceId, submitTool],
  )

  const cashierReady = cashier !== null || (snapshot?.current ?? null) !== null
  const vm = useMemo(
    () => buildOwnSaleVm({ resourceId, state, session: model, catalog: catalog.view, words, attempt, cashierReady }),
    [attempt, cashierReady, resourceId, state, model, catalog.view, words],
  )
  // Ключ окна оплаты и способ — для «Свернуть» / «Ход оплаты: …»: попытку без записи (журнал после F5) срез подхватывает этим ключом.
  const paymentKey = vm.payment?.attemptKey ?? null
  const paymentKind: PaymentKind = state.paidKind ?? attempt?.kind ?? state.paymentKind

  const actions = useMemo<SaleActions>(() => {
    const none = (): void => undefined
    return {
      reload: () => void agentActions.recheck(),
      addGood: (productKey) => {
        const row = catalog.view.search.rows.find((item) => item.productKey === productKey)
        if (row === undefined) addByKey(productKey)
        else addRow(row)
      },
      setQuantity: (key, quantity) => void dispatch(ownQuantityTyped({ resourceId, lineId: keyOf(key), text: quantity })),
      increment: (key) => void dispatch(ownQuantityStepped({ resourceId, lineId: keyOf(key), delta: 1 })),
      decrement: (key) => void dispatch(ownQuantityStepped({ resourceId, lineId: keyOf(key), delta: -1 })),
      removeLine: (key) => void dispatch(ownLineRemoved({ resourceId, lineId: keyOf(key) })),
      discardDraft: () => void dispatch(ownSaleCleared({ resourceId })),
      choosePaymentKind: (kind) => void dispatch(ownPaymentKindChosen({ resourceId, kind })),
      pay: () => payWith(state.paymentKind),
      fiscalize: none,
      cancelSale: () => void dispatch(ownSaleCleared({ resourceId })),
      cancelPayment: none,
      checkStatus: reconcile,
      reconcile,
      openManualResolve: none,
      // «Свернуть» и «Ход оплаты: …» — нажатия кассира в попытке; само окно не разворачивается (RP-PAY-ONE).
      collapsePayment: () => {
        if (paymentKey !== null) dispatch(ownPaymentCollapsed({ resourceId, id: paymentKey, kind: paymentKind }))
      },
      expandPayment: () => {
        if (paymentKey !== null) dispatch(ownPaymentExpanded({ resourceId, id: paymentKey, kind: paymentKind }))
      },
      // «Закрыть» отказа: корзина та же, её можно исправить и оплатить снова; «оплата не началась» до денег — окно закрыто до нажатия.
      closePayment: () => {
        if (state.phase === 'failed') dispatch(ownNewSaleStarted({ resourceId }))
        else dispatch(ownPaymentClosed({ resourceId }))
        agentActions.dismissNotice()
      },
      startNewSale: () => void dispatch(ownNewSaleStarted({ resourceId })),
      searchChanged: (query) => void dispatch(ownSearchChanged({ resourceId, text: query })),
      pageChanged: none,
      openScanner: none,
      receiptRetry: retryActions,
      park: parkActions,
      tool: toolActions,
      noticeAction: (key) => {
        if (key === 'print') printTail()
      },
      noticeDismiss: (key) => void dispatch(ownNoticeRemoved({ resourceId, key })),
      dismissFeedback: () => {
        if (state.phase === 'failed') dispatch(ownNewSaleStarted({ resourceId }))
        agentActions.dismissNotice()
      },
      openIssuedOutside: none,
      outsideReasonChanged: none,
      outsideReceiptChanged: none,
      submitIssuedOutside: none,
      closeIssuedOutside: none,
      addRow,
      searchTextChanged: (text) => void dispatch(ownSearchChanged({ resourceId, text })),
      submitSearch,
      selectLine: (key) => void dispatch(ownLineSelected({ resourceId, lineId: keyOf(key) })),
      contactChanged: (value) => void dispatch(ownContactChanged({ resourceId, text: value })),
      setPrice: none,
      resetPrice: none,
      payWith,
      hold: none,
      restoreHeld: none,
      dropHeld: none,
      clear: () => void dispatch(ownSaleCleared({ resourceId })),
    }
  }, [addByKey, addRow, agentActions, catalog.view.search.rows, dispatch, parkActions, payWith, paymentKey, paymentKind, printTail, reconcile, resourceId, retryActions, state.paymentKind, state.phase, submitSearch, toolActions])

  const choosePriceType = useCallback((key: string): void => void dispatch(ownPriceTypeChosen({ resourceId, key })), [dispatch, resourceId])
  // Вид цены меняется между чеками: посреди чека цены строк уже взяты по прежнему виду.
  const priceTypeLockedReason = state.phase !== 'composing' || state.cart.lines.length > 0 ? 'Вид цены меняется между чеками: закончите или отмените текущий чек.' : null
  return {
    vm,
    actions,
    priceTypes: catalog.priceTypes,
    priceTypeKey: state.priceTypeKey ?? catalog.defaultPriceTypeKey,
    priceTypeLockedReason,
    choosePriceType,
  }
}
